import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import StorageService from "../../services/StorageService";
import { API_URLS, resolvePublicFileUrl } from "../../constants/api-urls";
import "../../assets/styles/logistics_provider_view.scss";

const VIEW_TABS = [
  { key: "overview", label: "Overview" },
  { key: "capabilities", label: "Capabilities" },
  { key: "fleet", label: "Fleet & Assets" },
  { key: "coverage", label: "Coverage" },
  { key: "certifications", label: "Certifications" },
  { key: "performance", label: "Performance" },
  { key: "reviews", label: "Reviews" },
  { key: "contact", label: "Contact" },
];

const MODE_META = {
  road: { label: "Road", tone: "road", icon: "🚚" },
  ocean: { label: "Ocean", tone: "ocean", icon: "🚢" },
  rail: { label: "Rail", tone: "rail", icon: "🚆" },
  air: { label: "Air", tone: "air", icon: "✈" },
  multimodal: { label: "Multi-Modal", tone: "multi", icon: "🌐" },
};

const SPECIAL_CARGO_OPTIONS = [
  "Dangerous Goods",
  "Reefer / Cold Chain",
  "Oversized Cargo",
  "Perishables",
  "High-Value Security",
  "Live Animals",
  "Pharma Cold Chain",
  "Fragile Goods",
];

function normalizeLogisticsPayload(d) {
  if (!d) return null;
  const tm = d.transport_modes || {};
  const cd = d.contact_details || {};
  const cap = d.capabilities || {};
  const fa = d.fleet_assets || {};
  const legacyModes = [];
  if (tm.road) legacyModes.push("road");
  if (tm.sea) legacyModes.push("ocean");
  if (tm.rail) legacyModes.push("rail");
  if (tm.air) legacyModes.push("air");
  const modes = Array.isArray(tm.modes) ? tm.modes : legacyModes;
  const preferredShipmentTypes = Array.isArray(tm.preferredShipmentTypes)
    ? tm.preferredShipmentTypes
    : Array.isArray(tm.preferred_shipment_types)
      ? tm.preferred_shipment_types
      : [];
  const coreSpecialization = Array.isArray(cap.coreSpecialization)
    ? cap.coreSpecialization
    : Array.isArray(cap.core_specialization)
      ? cap.core_specialization
      : [];
  const industriesServed = Array.isArray(cap.industriesServed)
    ? cap.industriesServed
    : Array.isArray(cap.industries_served)
      ? cap.industries_served
      : [];
  const specialCargo =
    (Array.isArray(cap.specialCargoCapabilities) && cap.specialCargoCapabilities) ||
    (Array.isArray(cap.special_cargo_capabilities) && cap.special_cargo_capabilities) ||
    [];
  const ct = cap.commercialTerms || cap.commercial_terms || {};
  const commercialTerms = {
    pricingModelsOffered: Array.isArray(ct.pricingModelsOffered)
      ? ct.pricingModelsOffered
      : Array.isArray(ct.pricing_models_offered)
        ? ct.pricing_models_offered
        : [],
    paymentTerms: Array.isArray(ct.paymentTerms)
      ? ct.paymentTerms
      : Array.isArray(ct.payment_terms)
        ? ct.payment_terms
        : [],
    currenciesAccepted: Array.isArray(ct.currenciesAccepted)
      ? ct.currenciesAccepted
      : Array.isArray(ct.currencies_accepted)
        ? ct.currencies_accepted
        : [],
    creditPeriodDays: ct.creditPeriodDays ?? ct.credit_period_days ?? "",
    creditPeriodLabel: ct.creditPeriodLabel ?? ct.credit_period_label ?? "",
    commercialSupport: Array.isArray(ct.commercialSupport)
      ? ct.commercialSupport
      : Array.isArray(ct.commercial_support)
        ? ct.commercial_support
        : [],
    minimumRequirements: Array.isArray(ct.minimumRequirements)
      ? ct.minimumRequirements
      : Array.isArray(ct.minimum_requirements)
        ? ct.minimum_requirements
        : [],
  };
  const languagesSupported = Array.isArray(cd.languagesSupported)
    ? cd.languagesSupported
    : Array.isArray(cd.languages_supported)
      ? cd.languages_supported
      : [];
  const officeLocations = Array.isArray(cd.officeLocations)
    ? cd.officeLocations
    : Array.isArray(cd.office_locations)
      ? cd.office_locations
      : [];
  const businessHours = cd.businessHours || cd.business_hours || {};
  const communicationChannels = cd.communicationChannels || cd.communication_channels || {};
  const ws = fa.warehousesStorage || fa.warehouses_storage || {};
  const overview = fa.overview || fa.assetOverview || fa.asset_overview || {};
  const roadFleetBreakdown = fa.roadFleetBreakdown || fa.road_fleet_breakdown || {};
  const technologyTracking = fa.technologyTracking || fa.technology_tracking || {};
  const cr = d.coverage_routes || {};
  const geographicCoverage = Array.isArray(cr.geographicCoverage)
    ? cr.geographicCoverage
    : Array.isArray(cr.geographic_coverage)
      ? cr.geographic_coverage
      : [];
  const majorTradeLanes = Array.isArray(cr.majorTradeLanes)
    ? cr.majorTradeLanes
    : Array.isArray(cr.major_trade_lanes)
      ? cr.major_trade_lanes
      : [];
  const portsTerminals = Array.isArray(cr.portsTerminals)
    ? cr.portsTerminals
    : Array.isArray(cr.ports_terminals)
      ? cr.ports_terminals
      : [];
  const airportNetwork = Array.isArray(cr.airportNetwork)
    ? cr.airportNetwork
    : Array.isArray(cr.airport_network)
      ? cr.airport_network
      : [];
  const corridors = Array.isArray(cr.corridors) ? cr.corridors : Array.isArray(cr.majorTradeCorridors) ? cr.majorTradeCorridors : [];

  const normalizeGeo = (arr) => {
    const a = Array.isArray(arr) ? arr : [];
    return a
      .map((x) => {
        if (typeof x === "string") return { label: x.trim() };
        if (x && typeof x === "object") {
          const label =
            String(x.label || x.name || x.region || x.country || x.state || "").trim() ||
            String(x.title || "").trim();
          return label ? { label } : null;
        }
        return null;
      })
      .filter(Boolean);
  };

  const normalizeAirports = (arr) => {
    const a = Array.isArray(arr) ? arr : [];
    return a
      .map((x) => {
        if (typeof x === "string") {
          const s = x.trim();
          if (!s) return null;
          const tierMatch = s.match(/\bTier\s*[12]\b/i);
          const tier = tierMatch ? tierMatch[0].replace(/\s+/g, " ") : "";
          const label = tier ? s.replace(/\s*[-–]\s*Tier\s*[12]\b/i, "").trim() : s;
          return { label, tier };
        }
        if (x && typeof x === "object") {
          const label = String(x.label || x.name || x.airport || "").trim();
          const tier = String(x.tier || x.airportTier || x.airport_tier || "").trim();
          if (!label) return null;
          return { label, tier };
        }
        return null;
      })
      .filter(Boolean);
  };
  const cert = d.certifications || {};
  const industryCerts =
    cert.industryCertifications || cert.industry_certifications || cert.certifications || [];
  const serviceHighlights = Array.isArray(cap.serviceHighlights)
    ? cap.serviceHighlights
    : Array.isArray(cap.service_highlights)
      ? cap.service_highlights
      : [];
  const additionalServiceCapabilities =
    (Array.isArray(cap.additionalServiceCapabilities) && cap.additionalServiceCapabilities) ||
    (Array.isArray(cap.additional_service_capabilities) && cap.additional_service_capabilities) ||
    [];
  const operationalByMode = cap.operationalByMode || cap.operational_by_mode || {};
  const crRaw = cap.customerReviews || cap.customer_reviews || {};
  const distRaw = crRaw.ratingDistribution || crRaw.rating_distribution || {};
  const distNum = (k5, k4, snake) => {
    const v =
      distRaw[k5] ??
      distRaw[k4] ??
      (snake && distRaw[snake] !== undefined ? distRaw[snake] : "") ??
      "";
    const n = parseNum(v);
    return n != null && n >= 0 ? Math.round(n) : 0;
  };
  const customerReviews = {
    overallRating: String(crRaw.overallRating ?? crRaw.overall_rating ?? "").trim(),
    totalReviewsCount: String(crRaw.totalReviewsCount ?? crRaw.total_reviews_count ?? "").trim(),
    ratingDistribution: {
      5: distNum("5", 5, "stars_5"),
      4: distNum("4", 4, "stars_4"),
      3: distNum("3", 3, "stars_3"),
      2: distNum("2", 2, "stars_2"),
      1: distNum("1", 1, "stars_1"),
    },
    reviews: (() => {
      const arr = Array.isArray(crRaw.reviews)
        ? crRaw.reviews
        : Array.isArray(crRaw.reviews_list)
          ? crRaw.reviews_list
          : Array.isArray(crRaw.items)
            ? crRaw.items
            : [];
      return arr
        .map((it) => {
          if (!it || typeof it !== "object") return null;
          const companyName = String(
            it.companyName ?? it.company_name ?? it.reviewerCompany ?? it.reviewer_company ?? ""
          ).trim();
          const industry = String(it.industry ?? it.reviewer_industry ?? "").trim();
          const reviewDate = String(it.reviewDate ?? it.review_date ?? it.date ?? it.createdAt ?? it.created_at ?? "").trim();
          const comment = String(it.comment ?? it.body ?? it.text ?? it.review_text ?? "").trim();
          const verified = !!(it.isVerified ?? it.verified ?? it.is_verified);
          const rating = Math.max(0, Math.min(5, Math.round(parseNum(it.rating ?? it.stars) ?? 0)));
          const helpful = Math.max(0, Math.round(parseNum(it.helpfulCount ?? it.helpful_count) ?? 0));
          if (!companyName && !comment) return null;
          return { companyName, industry, reviewDate, comment, isVerified: verified, rating, helpfulCount: helpful };
        })
        .filter(Boolean);
    })(),
  };
  const perfRaw = cap.performance || cap.performance_kpis || {};
  const slRaw = perfRaw.serviceLevels || perfRaw.service_levels || {};
  const performance = {
    ...perfRaw,
    serviceLevels: {
      documentationAccuracyPercent:
        slRaw.documentationAccuracyPercent ?? slRaw.documentation_accuracy_percent ?? "",
      realTimeTrackingVisibilityPercent:
        slRaw.realTimeTrackingVisibilityPercent ?? slRaw.real_time_tracking_visibility_percent ?? "",
      customsClearanceSuccessPercent:
        slRaw.customsClearanceSuccessPercent ?? slRaw.customs_clearance_success_percent ?? "",
      customerSupportResponsePercent:
        slRaw.customerSupportResponsePercent ?? slRaw.customer_support_response_percent ?? "",
      cancellationRatePercent: slRaw.cancellationRatePercent ?? slRaw.cancellation_rate_percent ?? "",
      avgDelayHours: slRaw.avgDelayHours ?? slRaw.avg_delay_hours ?? "",
      documentationAccuracyTargetPercent:
        slRaw.documentationAccuracyTargetPercent ?? slRaw.documentation_accuracy_target_percent ?? "",
      realTimeTrackingTargetPercent:
        slRaw.realTimeTrackingTargetPercent ?? slRaw.real_time_tracking_target_percent ?? "",
      customsClearanceTargetPercent:
        slRaw.customsClearanceTargetPercent ?? slRaw.customs_clearance_target_percent ?? "",
      customerSupportTargetPercent:
        slRaw.customerSupportTargetPercent ?? slRaw.customer_support_target_percent ?? "",
      cancellationTargetPercent:
        slRaw.cancellationTargetPercent ?? slRaw.cancellation_target_percent ?? "",
      avgDelayTargetHours:
        slRaw.avgDelayTargetHours ?? slRaw.avg_delay_target_hours ?? "",
    },
  };
  return {
    basic_information: d.basic_information || {},
    transport_modes: { modes, preferredShipmentTypes },
    contact_details: {
      primaryName: cd.primaryName || cd.primary_name || "",
      primaryDesignation: cd.primaryDesignation || cd.role || cd.designation || "",
      primaryEmail: cd.primaryEmail || cd.email || "",
      primaryPhone: cd.primaryPhone || cd.phone || "",
      languagesSupported,
      customerSupport247:
        cd.customerSupport247 ?? cd.customer_support_247 ?? cd.customerSupport24x7 ?? false,
      officeLocations,
      businessHours,
      communicationChannels,
      availabilitySummary:
        String(cd.availabilitySummary ?? cd.availability_summary ?? cd.availabilityLine ?? cd.availability_line ?? "").trim(),
      timezone: String(cd.timezone ?? cd.business_hours_timezone ?? cd.businessHoursTimezone ?? "").trim(),
    },
    capabilities: {
      coreSpecialization,
      industriesServed,
      specialCargo,
      operatingModel: (d.basic_information || {}).operatingModel || (d.basic_information || {}).operating_model || "",
      commercialTerms,
      serviceHighlights,
      additionalServiceCapabilities,
      operationalByMode,
      performance,
      customerReviews,
    },
    fleet_assets: { warehousesStorage: ws, overview, roadFleetBreakdown, technologyTracking },
    coverage_routes: {
      geographicCoverage: normalizeGeo(geographicCoverage),
      majorTradeLanes,
      portsTerminals,
      airportNetwork: normalizeAirports(airportNetwork),
      corridors,
    },
    certifications: {
      registrations: cert.registrations || cert.companyRegistrations || {},
      industryCertifications: Array.isArray(industryCerts) ? industryCerts : [],
    },
  };
}

function pickStr(...vals) {
  for (const v of vals) {
    const s = String(v ?? "").trim();
    if (s) return s;
  }
  return "";
}

function parseNum(v) {
  const s = String(v ?? "").trim().replace(/,/g, "");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function fmtHoursShort(n) {
  if (n == null || Number.isNaN(n)) return "";
  const rounded = Math.abs(n - Math.round(n)) < 1e-9 ? Math.round(n) : Number(n.toFixed(1));
  return `${rounded} hrs`;
}

function hoursTextFromDb(v) {
  const raw = String(v ?? "").trim();
  if (!raw) return "";
  if (/hrs?/i.test(raw)) return raw;
  const n = parseNum(raw);
  if (n == null) return raw;
  return fmtHoursShort(n);
}

function customerRatingDisplay(v) {
  const raw = String(v ?? "").trim();
  if (!raw) return "";
  if (raw.includes("/")) return raw;
  const n = parseNum(raw);
  if (n == null) return raw;
  return `${n.toFixed(1)}/5.0`;
}

function joinPipe(parts) {
  const xs = (parts || []).map((x) => String(x ?? "").trim()).filter(Boolean);
  if (!xs.length) return "";
  return xs.join(" | ");
}

function clampPctWidth(n) {
  if (n == null || Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

function barWidthForLowerIsBetter(value, good, warn) {
  const v = parseNum(value);
  const g = parseNum(good);
  const w = parseNum(warn);
  if (v == null) return 55;
  if (g != null && v <= g) return 92;
  if (w != null && v >= w) return 35;
  if (g != null && w != null && w > g) {
    const t = (v - g) / (w - g);
    return clampPctWidth(92 - Math.max(0, Math.min(1, t)) * 57);
  }
  return clampPctWidth(100 - v);
}

function PerfSparkIcon() {
  return (
    <svg className="lpv-perf-ico-svg" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M4 16c3.5-1.2 6-5.2 8-8.5 1.7 3.1 4.3 6.5 8 8.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PerfClockIcon() {
  return (
    <svg className="lpv-perf-ico-svg" viewBox="0 0 24 24" aria-hidden>
      <circle cx="12" cy="13" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M12 10v3.5l2.2 1.3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 5V3M18 6l1.2-1.2M6 6L4.8 4.8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function PerfStarIcon() {
  return (
    <svg className="lpv-perf-ico-svg" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M12 3.5l2.2 5.5h5.8l-4.7 3.4 1.8 5.6L12 16.9 7.9 18l1.8-5.6L5 9H10.8L12 3.5z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PerfRepeatIcon() {
  return (
    <svg className="lpv-perf-ico-svg" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M5 9a6 6 0 0 1 10.24-4.24M19 15a6 6 0 0 1-10.24 4.24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path d="M5 4v5h5M19 20v-5h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function PerfMoneyIcon() {
  return (
    <svg className="lpv-perf-ico-svg" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M12 3v18M16 7.2c0-1.2-1.8-2.2-4-2.2s-4 1-4 2.2 1.8 2.2 4 2.2 4 1 4 2.2-1.8 2.2-4 2.2-4-1-4-2.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PerfCardIcon() {
  return (
    <svg className="lpv-perf-ico-svg" viewBox="0 0 24 24" aria-hidden>
      <rect x="3" y="6" width="18" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M3 10h18" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function PerfBriefIcon() {
  return (
    <svg className="lpv-perf-ico-svg" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M7 7h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  );
}

function PerfAlertIcon() {
  return (
    <svg className="lpv-perf-ico-svg" viewBox="0 0 24 24" aria-hidden>
      <path d="M12 3l10 18H2L12 3z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M12 9v5M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function renderLogisticsPerformanceTab(profile) {
  const perf = profile?.capabilities?.performance || {};
  const sl = perf.serviceLevels || {};
  const ind = perf.industryTargets || perf.industry_targets || {};
  const ct = profile?.capabilities?.commercialTerms || {};

  const onTime = pickStr(perf.onTimeDeliveryPercent, perf.on_time_delivery_percent);
  const onTimeTarget = pickStr(perf.onTimeDeliveryTargetPercent, perf.on_time_delivery_target_percent);
  const onTimeInd = pickStr(perf.onTimeIndustryAvgPercent, perf.on_time_industry_avg_percent);
  const onTimeBadge = pickStr(perf.onTimeDeliveryBadge, perf.on_time_delivery_badge);

  const respH = pickStr(perf.avgResponseHours, perf.avg_response_hours);
  const respTarget = pickStr(perf.quoteResponseTargetHours, perf.quote_response_target_hours);
  const respInd = pickStr(perf.quoteResponseIndustryAvgHours, perf.quote_response_industry_avg_hours);
  const respBadge = pickStr(perf.quoteResponseBadge, perf.quote_response_badge);

  const rating = pickStr(perf.customerRating, perf.customer_rating);
  const ratingBadge = pickStr(perf.customerRatingBadge, perf.customer_rating_badge);
  const reviews = pickStr(perf.customerReviewsCount, perf.customer_reviews_count);
  const recPct = pickStr(perf.customerRecommendedPercent, perf.customer_recommended_percent);

  const repeat = pickStr(perf.repeatCustomersPercent, perf.repeat_customers_percent);
  const repeatInd = pickStr(perf.repeatCustomersIndustryAvgPercent, perf.repeat_customers_industry_avg_percent);
  const repeatBadge = pickStr(perf.repeatCustomersBadge, perf.repeat_customers_badge);

  const booking = pickStr(perf.bookingConfirmationHours, perf.booking_confirmation_hours);
  const shipOk = pickStr(perf.shipmentSuccessPercent, perf.shipment_success_percent);
  const claims = pickStr(perf.claimsRatioPercent, perf.claims_ratio_percent);

  const onTimeN = parseNum(onTime);
  const onTimeBar = onTimeN == null ? 0 : clampPctWidth(onTimeN);
  const respN = parseNum(respH);
  const respBar = respN == null ? 0 : clampPctWidth(Math.max(0, Math.min(100, 100 - (respN / 6) * 100)));
  const ratingN = parseNum(rating);
  const ratingBar = ratingN == null ? 0 : clampPctWidth((ratingN / 5) * 100);
  const repeatN = parseNum(repeat);
  const repeatBar = repeatN == null ? 0 : clampPctWidth(repeatN);

  const shipN = parseNum(shipOk);
  const claimsN = parseNum(claims);
  const bookingN = parseNum(booking);

  const docV = pickStr(sl.documentationAccuracyPercent, sl.documentation_accuracy_percent);
  const docT = pickStr(sl.documentationAccuracyTargetPercent, sl.documentation_accuracy_target_percent);
  const docI = pickStr(ind.documentationAccuracyPercent, ind.documentation_accuracy_percent);

  const rtV = pickStr(sl.realTimeTrackingVisibilityPercent, sl.real_time_tracking_visibility_percent);
  const rtT = pickStr(sl.realTimeTrackingTargetPercent, sl.real_time_tracking_target_percent);
  const rtI = pickStr(ind.realTimeTrackingVisibilityPercent, ind.real_time_tracking_visibility_percent);

  const ccV = pickStr(sl.customsClearanceSuccessPercent, sl.customs_clearance_success_percent);
  const ccT = pickStr(sl.customsClearanceTargetPercent, sl.customs_clearance_target_percent);
  const ccI = pickStr(ind.customsClearanceSuccessPercent, ind.customs_clearance_success_percent);

  const csV = pickStr(sl.customerSupportResponsePercent, sl.customer_support_response_percent);
  const csT = pickStr(sl.customerSupportTargetPercent, sl.customer_support_target_percent);
  const csI = pickStr(ind.customerSupportResponsePercent, ind.customer_support_response_percent);

  const canV = pickStr(sl.cancellationRatePercent, sl.cancellation_rate_percent);
  const canT = pickStr(sl.cancellationTargetPercent, sl.cancellation_target_percent);
  const canI = pickStr(ind.cancellationRatePercent, ind.cancellation_rate_percent);

  const delayV = pickStr(sl.avgDelayHours, sl.avg_delay_hours);
  const delayT = pickStr(sl.avgDelayTargetHours, sl.avg_delay_target_hours);
  const delayI = pickStr(ind.avgDelayHours, ind.avg_delay_hours);

  const pricing = Array.isArray(ct.pricingModelsOffered) ? ct.pricingModelsOffered.map(String) : [];
  const pay = Array.isArray(ct.paymentTerms) ? ct.paymentTerms.map(String) : [];
  const cur = Array.isArray(ct.currenciesAccepted) ? ct.currenciesAccepted.map(String) : [];
  const creditDays = pickStr(ct.creditPeriodDays, ct.credit_period_days);
  const creditLabel = pickStr(ct.creditPeriodLabel, ct.credit_period_label);
  const support = Array.isArray(ct.commercialSupport) ? ct.commercialSupport.map(String) : [];
  const mins = Array.isArray(ct.minimumRequirements) ? ct.minimumRequirements.map(String) : [];

  const payLines = [];
  if (creditLabel) payLines.push(`Credit Period: ${creditLabel}`);
  else if (creditDays) payLines.push(`Credit Period: ${creditDays} days`);
  payLines.push(...pay);
  if (cur.length) payLines.push(`Currency: ${cur.join(", ")}`);

  const hasAnyPerf =
    !!pickStr(
      onTime,
      respH,
      rating,
      reviews,
      recPct,
      repeat,
      booking,
      shipOk,
      claims,
      docV,
      rtV,
      ccV,
      csV,
      canV,
      delayV
    );

  const pill = (tone, text) =>
    text ? <span className={`lpv-perf-pill lpv-perf-pill--${tone}`}>{text}</span> : null;

  const bar = (width) => (
    <div className="lpv-perf-bar" aria-hidden>
      <span style={{ width: `${clampPctWidth(width)}%` }} />
    </div>
  );

  const fmtMaybePct = (raw) => {
    const s = String(raw ?? "").trim();
    if (!s) return "—";
    if (s.includes("%")) return s;
    const n = parseNum(s);
    if (n == null) return s;
    return `${n}%`;
  };

  return (
    <section className="lpv-panel card border-0 shadow-sm lpv-perf-panel">
      {!hasAnyPerf ? <div className="lpv-perf-empty text-muted">Performance metrics are not published yet.</div> : null}

      <div className="lpv-perf-sec">
        <div className="lpv-perf-sec-title">Performance &amp; Reliability Metrics</div>
        <div className="lpv-perf-top-grid">
          <div className="lpv-perf-hero lpv-perf-hero--green">
            <div className="lpv-perf-hero-top">
              <div>
                <div className="lpv-perf-hero-k">On-Time Delivery</div>
                <div className="lpv-perf-hero-v">{onTime ? `${onTime}%` : "—"}</div>
              </div>
              <div className="lpv-perf-hero-ico">
                <PerfSparkIcon />
              </div>
            </div>
            <div className="lpv-perf-hero-meta">{pill("green", onTimeBadge)}</div>
            {bar(onTimeBar)}
            <div className="lpv-perf-hero-foot">
              {joinPipe([
                onTimeTarget ? `Target: ${String(onTimeTarget).includes("%") ? onTimeTarget : `${onTimeTarget}%`}` : "",
                onTimeInd ? `Industry avg: ${String(onTimeInd).includes("%") ? onTimeInd : `${onTimeInd}%`}` : "",
              ]) || "—"}
            </div>
          </div>

          <div className="lpv-perf-hero lpv-perf-hero--blue">
            <div className="lpv-perf-hero-top">
              <div>
                <div className="lpv-perf-hero-k">Quote Response Time</div>
                <div className="lpv-perf-hero-v">{hoursTextFromDb(respH) || "—"}</div>
              </div>
              <div className="lpv-perf-hero-ico">
                <PerfClockIcon />
              </div>
            </div>
            <div className="lpv-perf-hero-meta">{pill("blue", respBadge)}</div>
            {bar(respBar)}
            <div className="lpv-perf-hero-foot">
              {joinPipe([
                respTarget ? `Target: ${hoursTextFromDb(respTarget) || respTarget}` : "",
                respInd ? `Industry avg: ${hoursTextFromDb(respInd) || respInd}` : "",
              ]) || "—"}
            </div>
          </div>

          <div className="lpv-perf-hero lpv-perf-hero--purple">
            <div className="lpv-perf-hero-top">
              <div>
                <div className="lpv-perf-hero-k">Customer Rating</div>
                <div className="lpv-perf-hero-v">{customerRatingDisplay(rating) || "—"}</div>
              </div>
              <div className="lpv-perf-hero-ico">
                <PerfStarIcon />
              </div>
            </div>
            <div className="lpv-perf-hero-meta">{pill("purple", ratingBadge)}</div>
            {bar(ratingBar)}
            <div className="lpv-perf-hero-foot">
              {joinPipe([
                reviews ? `${reviews} reviews` : "",
                recPct ? `${String(recPct).includes("%") ? recPct : `${recPct}%`} recommended` : "",
              ]) || "—"}
            </div>
          </div>

          <div className="lpv-perf-hero lpv-perf-hero--yellow">
            <div className="lpv-perf-hero-top">
              <div>
                <div className="lpv-perf-hero-k">Repeat Customers</div>
                <div className="lpv-perf-hero-v">{repeat ? `${repeat}%` : "—"}</div>
              </div>
              <div className="lpv-perf-hero-ico">
                <PerfRepeatIcon />
              </div>
            </div>
            <div className="lpv-perf-hero-meta">{pill("yellow", repeatBadge)}</div>
            {bar(repeatBar)}
            <div className="lpv-perf-hero-foot">
              {joinPipe([repeatInd ? `Industry avg: ${String(repeatInd).includes("%") ? repeatInd : `${repeatInd}%`}` : ""]) || "—"}
            </div>
          </div>
        </div>
      </div>

      <div className="lpv-perf-sec">
        <div className="lpv-perf-sec-title">Operational Metrics</div>
        <div className="lpv-perf-op-grid">
          <div className="lpv-perf-op-card">
            <div className="lpv-perf-op-top">
              <div>
                <div className="lpv-perf-op-k">Booking Confirmation</div>
                <div className="lpv-perf-op-v">{hoursTextFromDb(booking) || "—"}</div>
              </div>
              <div className="lpv-perf-op-ico lpv-perf-op-ico--ok" aria-hidden>
                ✓
              </div>
            </div>
            {bar(bookingN == null ? 55 : clampPctWidth(100 - (bookingN / 8) * 100))}
            <div className="lpv-perf-op-foot">Average confirmation time</div>
          </div>

          <div className="lpv-perf-op-card">
            <div className="lpv-perf-op-top">
              <div>
                <div className="lpv-perf-op-k">Shipment Success</div>
                <div className="lpv-perf-op-v">{shipOk ? `${shipOk}%` : "—"}</div>
              </div>
              <div className="lpv-perf-op-ico lpv-perf-op-ico--ok" aria-hidden>
                ✓
              </div>
            </div>
            {bar(shipN == null ? 60 : clampPctWidth(shipN))}
            <div className="lpv-perf-op-foot">Successful deliveries</div>
          </div>

          <div className="lpv-perf-op-card">
            <div className="lpv-perf-op-top">
              <div>
                <div className="lpv-perf-op-k">Claims Ratio</div>
                <div className="lpv-perf-op-v">{claims ? `${claims}%` : "—"}</div>
              </div>
              <div className="lpv-perf-op-ico lpv-perf-op-ico--bad" aria-hidden>
                ×
              </div>
            </div>
            {bar(claimsN == null ? 25 : barWidthForLowerIsBetter(claimsN, 0.5, 3))}
            <div className="lpv-perf-op-foot">Damage/loss claims</div>
          </div>
        </div>
      </div>

      <div className="lpv-perf-sec">
        <div className="lpv-perf-sec-title">Service Level Performance</div>
        <div className="lpv-perf-svc">
          <div className="lpv-perf-svc-row">
            <div className="lpv-perf-svc-left">
              <div className="lpv-perf-svc-t">Documentation Accuracy</div>
              <div className="lpv-perf-svc-sub">
                {joinPipe([
                  docT ? `Target: ${String(docT).includes("%") ? docT : `${docT}%`}` : "",
                  docI ? `Industry Target: ${String(docI).includes("%") ? docI : `${docI}%`}` : "",
                ]) || "—"}
              </div>
            </div>
            <div className="lpv-perf-svc-bar">{bar(parseNum(docV) ?? 0)}</div>
            <div className="lpv-perf-svc-val">{fmtMaybePct(docV)}</div>
          </div>

          <div className="lpv-perf-svc-row">
            <div className="lpv-perf-svc-left">
              <div className="lpv-perf-svc-t">Real-time Tracking Visibility</div>
              <div className="lpv-perf-svc-sub">
                {joinPipe([
                  rtT ? `Target: ${String(rtT).includes("%") ? rtT : `${rtT}%`}` : "",
                  rtI ? `Industry Target: ${String(rtI).includes("%") ? rtI : `${rtI}%`}` : "",
                ]) || "—"}
              </div>
            </div>
            <div className="lpv-perf-svc-bar">{bar(parseNum(rtV) ?? 0)}</div>
            <div className="lpv-perf-svc-val">{fmtMaybePct(rtV)}</div>
          </div>

          <div className="lpv-perf-svc-row">
            <div className="lpv-perf-svc-left">
              <div className="lpv-perf-svc-t">Customs Clearance Success</div>
              <div className="lpv-perf-svc-sub">
                {joinPipe([
                  ccT ? `Target: ${String(ccT).includes("%") ? ccT : `${ccT}%`}` : "",
                  ccI ? `Industry Target: ${String(ccI).includes("%") ? ccI : `${ccI}%`}` : "",
                ]) || "—"}
              </div>
            </div>
            <div className="lpv-perf-svc-bar">{bar(parseNum(ccV) ?? 0)}</div>
            <div className="lpv-perf-svc-val">{fmtMaybePct(ccV)}</div>
          </div>

          <div className="lpv-perf-svc-row">
            <div className="lpv-perf-svc-left">
              <div className="lpv-perf-svc-t">Customer Support Response</div>
              <div className="lpv-perf-svc-sub">
                {joinPipe([
                  csT ? `Target: ${String(csT).includes("%") ? csT : `${csT}%`}` : "",
                  csI ? `Industry Target: ${String(csI).includes("%") ? csI : `${csI}%`}` : "",
                ]) || "—"}
              </div>
            </div>
            <div className="lpv-perf-svc-bar">{bar(parseNum(csV) ?? 0)}</div>
            <div className="lpv-perf-svc-val">{fmtMaybePct(csV)}</div>
          </div>

          <div className="lpv-perf-svc-row">
            <div className="lpv-perf-svc-left">
              <div className="lpv-perf-svc-t">Cancellation Rate</div>
              <div className="lpv-perf-svc-sub">
                {joinPipe([
                  canT ? `Target: ${String(canT).includes("%") ? canT : `${canT}%`}` : "",
                  canI ? `Industry Target: ${String(canI).includes("%") ? canI : `${canI}%`}` : "",
                ]) || "—"}
              </div>
            </div>
            <div className="lpv-perf-svc-bar">{bar(parseNum(canV) ?? 0)}</div>
            <div className="lpv-perf-svc-val">{fmtMaybePct(canV)}</div>
          </div>

          <div className="lpv-perf-svc-row">
            <div className="lpv-perf-svc-left">
              <div className="lpv-perf-svc-t">Average Delay Time</div>
              <div className="lpv-perf-svc-sub">
                {joinPipe([
                  delayT ? `Target: ${hoursTextFromDb(delayT) || delayT}` : "",
                  delayI ? `Industry Target: ${hoursTextFromDb(delayI) || delayI}` : "",
                ]) || "—"}
              </div>
            </div>
            <div className="lpv-perf-svc-bar">
              {bar(barWidthForLowerIsBetter(parseNum(delayV), parseNum(delayT), parseNum(delayI)))}
            </div>
            <div className="lpv-perf-svc-val">{hoursTextFromDb(delayV) || delayV || "—"}</div>
          </div>
        </div>
      </div>

      <div className="lpv-perf-sec">
        <div className="lpv-perf-sec-title">Pricing &amp; Commercial Terms</div>
        <div className="lpv-perf-comm-grid">
          <div className="lpv-perf-comm lpv-perf-comm--blue">
            <div className="lpv-perf-comm-head">
              <div className="lpv-perf-comm-ico">
                <PerfMoneyIcon />
              </div>
              <div className="lpv-perf-comm-title">Pricing Models</div>
            </div>
            <ul className="lpv-perf-list">
              {(pricing.length ? pricing : ["—"]).map((x, i) => (
                <li key={`${x}-${i}`}>
                  <span className="lpv-perf-li-ico lpv-perf-li-ico--ok" aria-hidden>
                    ✓
                  </span>
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lpv-perf-comm lpv-perf-comm--green">
            <div className="lpv-perf-comm-head">
              <div className="lpv-perf-comm-ico">
                <PerfCardIcon />
              </div>
              <div className="lpv-perf-comm-title">Payment Terms</div>
            </div>
            <ul className="lpv-perf-list">
              {(payLines.length ? payLines : ["—"]).map((x, i) => (
                <li key={`${x}-${i}`}>
                  <span className="lpv-perf-li-ico lpv-perf-li-ico--info" aria-hidden>
                    i
                  </span>
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lpv-perf-comm lpv-perf-comm--pink">
            <div className="lpv-perf-comm-head">
              <div className="lpv-perf-comm-ico">
                <PerfBriefIcon />
              </div>
              <div className="lpv-perf-comm-title">Commercial Support</div>
            </div>
            <ul className="lpv-perf-list">
              {(support.length ? support : ["—"]).map((x, i) => (
                <li key={`${x}-${i}`}>
                  <span className="lpv-perf-li-ico lpv-perf-li-ico--ok" aria-hidden>
                    ✓
                  </span>
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lpv-perf-comm lpv-perf-comm--yellow">
            <div className="lpv-perf-comm-head">
              <div className="lpv-perf-comm-ico">
                <PerfAlertIcon />
              </div>
              <div className="lpv-perf-comm-title">Minimum Requirements</div>
            </div>
            <ul className="lpv-perf-list">
              {(mins.length ? mins : ["—"]).map((x, i) => (
                <li key={`${x}-${i}`}>
                  <span className="lpv-perf-li-ico lpv-perf-li-ico--info" aria-hidden>
                    i
                  </span>
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function formatReviewMonthYear(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "—";
  if (/^[A-Za-z]+\s+\d{4}$/.test(s)) return s;
  const t = Date.parse(s);
  if (!Number.isNaN(t)) {
    return new Date(t).toLocaleString("en-US", { month: "long", year: "numeric" });
  }
  return s;
}

function parseReviewDateMs(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return 0;
  const t = Date.parse(s);
  return Number.isNaN(t) ? 0 : t;
}

function RevStarsRow({ rating }) {
  const r = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
  return (
    <div className="lpv-rev-stars" aria-label={`${r} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={i <= r ? "lpv-rev-star lpv-rev-star--on" : "lpv-rev-star"}>
          ★
        </span>
      ))}
    </div>
  );
}

function RevTabStarIcon() {
  return (
    <svg className="lpv-rev-title-ico" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M12 2.5l2.8 6.9h7.4l-6 4.6 2.3 7-6.5-4.7-6.5 4.7 2.3-7-6-4.6h7.4L12 2.5z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg className="lpv-rev-load-ico" viewBox="0 0 24 24" aria-hidden>
      <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function LogisticsReviewsTab({ profile }) {
  const [filter, setFilter] = useState("all");
  const [visibleCount, setVisibleCount] = useState(4);

  const cr = profile?.capabilities?.customerReviews;
  const perf = profile?.capabilities?.performance || {};
  const itemsRaw = Array.isArray(cr?.reviews) ? cr.reviews : [];

  const derivedDist = useMemo(() => {
    const base = {
      5: Number(cr?.ratingDistribution?.[5] ?? 0) || 0,
      4: Number(cr?.ratingDistribution?.[4] ?? 0) || 0,
      3: Number(cr?.ratingDistribution?.[3] ?? 0) || 0,
      2: Number(cr?.ratingDistribution?.[2] ?? 0) || 0,
      1: Number(cr?.ratingDistribution?.[1] ?? 0) || 0,
    };
    const sum = base[1] + base[2] + base[3] + base[4] + base[5];
    if (sum > 0 || !itemsRaw.length) return base;
    const out = { ...base };
    itemsRaw.forEach((it) => {
      const r = Math.max(1, Math.min(5, Math.round(Number(it?.rating) || 0)));
      if (r) out[r] += 1;
    });
    return out;
  }, [cr?.ratingDistribution, itemsRaw]);

  const maxDist = useMemo(
    () => Math.max(1, derivedDist[1], derivedDist[2], derivedDist[3], derivedDist[4], derivedDist[5]),
    [derivedDist]
  );

  const sortedFiltered = useMemo(() => {
    let list = itemsRaw.map((it, idx) => ({ ...it, _idx: idx }));
    if (filter === "verified") {
      list = list.filter((it) => it.isVerified);
    }
    if (filter === "recent") {
      list = [...list].sort((a, b) => parseReviewDateMs(b.reviewDate) - parseReviewDateMs(a.reviewDate));
    } else if (filter === "highest") {
      list = [...list].sort((a, b) => {
        const rd = (Number(b.rating) || 0) - (Number(a.rating) || 0);
        if (rd !== 0) return rd;
        return parseReviewDateMs(b.reviewDate) - parseReviewDateMs(a.reviewDate);
      });
    }
    return list;
  }, [itemsRaw, filter]);

  useEffect(() => {
    setVisibleCount(4);
  }, [filter, itemsRaw.length]);

  const overall =
    pickStr(cr?.overallRating) ||
    (perf?.customerRating != null && String(perf.customerRating).trim() ? customerRatingDisplay(perf.customerRating) : "");
  const totalStr = pickStr(cr?.totalReviewsCount) || pickStr(perf?.customerReviewsCount);
  const totalLabel = totalStr ? `${Number(totalStr) || totalStr} Reviews` : "";

  const visible = sortedFiltered.slice(0, visibleCount);
  const canLoadMore = visibleCount < sortedFiltered.length;

  return (
    <section className="lpv-panel card border-0 shadow-sm lpv-rev-panel">
      <div className="lpv-rev-head">
        <div className="lpv-rev-head-left">
          <div className="lpv-rev-title-row">
            <RevTabStarIcon />
            <h2 className="lpv-rev-title">Customer Reviews &amp; Testimonials</h2>
          </div>
          <p className="lpv-rev-sub">Feedback from businesses we&apos;ve served.</p>
        </div>
        <div className="lpv-rev-head-right">
          <div className="lpv-rev-agg">{overall || "—"}</div>
          {totalLabel ? <div className="lpv-rev-agg-sub">{totalLabel}</div> : null}
        </div>
      </div>

      <div className="lpv-rev-dist">
        {[5, 4, 3, 2, 1].map((stars) => {
          const count = derivedDist[stars] || 0;
          const pct = maxDist ? Math.round((count / maxDist) * 100) : 0;
          return (
            <div key={stars} className="lpv-rev-dist-row">
              <span className="lpv-rev-dist-n">{stars}</span>
              <span className="lpv-rev-dist-star" aria-hidden>
                ★
              </span>
              <div className="lpv-rev-dist-bar" aria-hidden>
                <span style={{ width: `${clampPctWidth(pct)}%` }} />
              </div>
              <span className="lpv-rev-dist-cnt">{count}</span>
            </div>
          );
        })}
      </div>

      <div className="lpv-rev-chips" role="tablist" aria-label="Review filters">
        {[
          { id: "all", label: "All Reviews" },
          { id: "recent", label: "Most Recent" },
          { id: "highest", label: "Highest Rated" },
          { id: "verified", label: "Verified Only" },
        ].map((c) => (
          <button
            key={c.id}
            type="button"
            role="tab"
            aria-selected={filter === c.id}
            className={`lpv-rev-chip ${filter === c.id ? "is-active" : ""}`}
            onClick={() => setFilter(c.id)}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="lpv-rev-list">
        {!sortedFiltered.length ? (
          <div className="lpv-rev-empty text-muted">No reviews published yet.</div>
        ) : (
          visible.map((it, i) => (
            <article key={`${it._idx}-${i}-${it.companyName}`} className="lpv-rev-card">
              <div className="lpv-rev-card-top">
                <div className="lpv-rev-card-who">
                  <div className="lpv-rev-co-row">
                    <span className="lpv-rev-co">{it.companyName || "—"}</span>
                    {it.isVerified ? (
                      <span className="lpv-rev-verified">
                        <span aria-hidden>✓</span> Verified
                      </span>
                    ) : null}
                  </div>
                  {it.industry ? <div className="lpv-rev-ind">{it.industry}</div> : null}
                </div>
                <div className="lpv-rev-date">{formatReviewMonthYear(it.reviewDate)}</div>
              </div>
              <RevStarsRow rating={it.rating} />
              {it.comment ? <p className="lpv-rev-body">{it.comment}</p> : null}
              <div className="lpv-rev-foot">
                <button type="button" className="lpv-rev-helpful">
                  <svg className="lpv-rev-thumb" viewBox="0 0 24 24" aria-hidden>
                    <path
                      d="M7 11V20H4V11H7ZM8 11H17.2C18.2 11 19 11.8 19 12.8V14L20.2 17.4C20.6 18.5 19.8 19.7 18.6 19.7H11V11L13.5 4.2C13.7 3.6 13.2 3 12.5 3 11.9 3 11.4 3.4 11.2 4L8 11Z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Helpful ({Number(it.helpfulCount) || 0})
                </button>
              </div>
            </article>
          ))
        )}
      </div>

      {canLoadMore ? (
        <div className="lpv-rev-more-wrap">
          <button type="button" className="lpv-rev-load" onClick={() => setVisibleCount((n) => n + 4)}>
            Load More Reviews
            <ChevronDownIcon />
          </button>
        </div>
      ) : null}
    </section>
  );
}

function initialsFromPersonName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  const a = (parts[0][0] || "?").toUpperCase();
  const b = (parts.length > 1 ? parts[parts.length - 1][0] : parts[0][1] || "").toUpperCase();
  return `${a}${b}`.slice(0, 2);
}

function buildContactHoursLine(cd) {
  const summary = String(cd?.availabilitySummary || "").trim();
  if (summary) return summary;
  const bh = cd?.businessHours || {};
  const mf = String(bh.monFri || "").trim();
  const sat = String(bh.saturday || "").trim();
  const sun = String(bh.sunday || "").trim();
  const tz = String(cd?.timezone || "").trim();
  const bits = [];
  if (mf) bits.push(`Mon–Fri: ${mf}`);
  if (sat) bits.push(`Sat: ${sat}`);
  if (sun) bits.push(`Sun: ${sun}`);
  let line = bits.join(" · ");
  if (tz && line) line = `${line} ${tz}`;
  return line || "—";
}

function IcoPhone() {
  return (
    <svg className="lpv-contact-ico" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M6.6 3h3.2l1.4 4.2-2.1 1.3c.9 1.7 2.3 3.1 4 4l1.3-2.1L21 7.8v3.2c0 6-4.9 11-11 11h-.2C4.9 22 2 19.1 2 15.5 2 8.9 6.6 3 6.6 3z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IcoEmail() {
  return (
    <svg className="lpv-contact-ico" viewBox="0 0 24 24" aria-hidden>
      <path d="M4 6h16v12H4V6zm0 0l8 7 8-7" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    </svg>
  );
}

function IcoClock() {
  return (
    <svg className="lpv-contact-ico" viewBox="0 0 24 24" aria-hidden>
      <circle cx="12" cy="13" r="7" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path d="M12 10v3.5l2.2 1.2M12 5V3" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function IcoBuilding() {
  return (
    <svg className="lpv-contact-ico lpv-contact-ico--sec" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M4 20h16M6 20V8l6-2v14M12 20V10h6v10M9 13h1M9 16h1M15 13h1M15 16h1"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IcoChatSec() {
  return (
    <svg className="lpv-contact-ico lpv-contact-ico--sec" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M5 5h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-5l-4 3v-3H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IcoMsg() {
  return (
    <svg className="lpv-contact-qa-ico" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M5 4h14a2 2 0 0 1 2 2v10l-4-3H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IcoDownload() {
  return (
    <svg className="lpv-contact-qa-ico" viewBox="0 0 24 24" aria-hidden>
      <path d="M12 4v10m0 0l4-4m-4 4L8 10M5 20h14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function IcoBookmark() {
  return (
    <svg className="lpv-contact-qa-ico" viewBox="0 0 24 24" aria-hidden>
      <path d="M6 3h12v18l-6-4-6 4V3z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    </svg>
  );
}

function IcoCompare() {
  return (
    <svg className="lpv-contact-qa-ico" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M7 4h4v16H7V4zm6 4h4v12h-4V8zM4 20h16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IcoTilePhone() {
  return (
    <svg className="lpv-contact-tile-ico" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M8 3h2.5l1.1 3.3-1.7 1c.7 1.4 1.8 2.5 3.1 3.1l1-1.7L17 9.5V12c0 4.4-3.6 8-8 8H9c-4.4 0-8-3.6-8-8V8c0-2.8 2.2-5 5-5z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IcoTileEmail() {
  return (
    <svg className="lpv-contact-tile-ico" viewBox="0 0 24 24" aria-hidden>
      <path d="M4 7h16v10H4V7zm0 0l8 5.5L20 7" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

function IcoTileChat() {
  return (
    <svg className="lpv-contact-tile-ico" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M6 5h12a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-4l-3 3v-3H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IcoTileCalendar() {
  return (
    <svg className="lpv-contact-tile-ico" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M7 4V2M17 4V2M5 9h14M6 5h12a2 2 0 0 1 2 2v11H4V7a2 2 0 0 1 2-2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function LogisticsContactTab({ profile, company }) {
  const cd = profile?.contact_details || {};
  const ch = cd.communicationChannels || {};
  const nameRaw = String(cd.primaryName || "").trim();
  const name = nameRaw || "—";
  const title = String(cd.primaryDesignation || "").trim() || "—";
  const email = String(cd.primaryEmail || "").trim();
  const phone = String(cd.primaryPhone || "").trim();
  const hoursLine = buildContactHoursLine(cd);
  const offices = Array.isArray(cd.officeLocations) ? cd.officeLocations : [];
  const bh = cd.businessHours || {};
  const brochure =
    company?.media?.brochure ||
    company?.media?.company_brochure ||
    company?.brochure ||
    profile?.basic_information?.companyBrochure ||
    profile?.basic_information?.company_brochure;
  const brochureUrl = brochure ? resolvePublicFileUrl(String(brochure)) : "";

  return (
    <div className="lpv-contact-page">
      <div className="lpv-contact-main">
        <header className="lpv-contact-hero">
          <div className="lpv-contact-hero-ico-wrap" aria-hidden>
            <IcoPhone />
          </div>
          <div>
            <h2 className="lpv-contact-hero-title">Contact Information</h2>
            <p className="lpv-contact-hero-sub">Get in touch with our business development team</p>
          </div>
        </header>

        <div className="lpv-contact-primary">
          <div className="lpv-contact-avatar" aria-hidden>
            {nameRaw ? initialsFromPersonName(nameRaw) : "?"}
          </div>
          <div className="lpv-contact-primary-body">
            <div className="lpv-contact-name">{name}</div>
            <div className="lpv-contact-role">{title}</div>
            <div className="lpv-contact-rows">
              <div className="lpv-contact-row">
                <IcoPhone />
                <span>
                  {phone ? (
                    <a className="lpv-contact-link" href={`tel:${phone.replace(/\s/g, "")}`}>
                      {phone}
                    </a>
                  ) : (
                    "—"
                  )}
                </span>
              </div>
              <div className="lpv-contact-row">
                <IcoEmail />
                <span>
                  {email ? (
                    <a className="lpv-contact-link" href={`mailto:${email}`}>
                      {email}
                    </a>
                  ) : (
                    "—"
                  )}
                </span>
              </div>
              <div className="lpv-contact-row">
                <IcoClock />
                <span>{hoursLine}</span>
              </div>
            </div>
          </div>
        </div>

        <section className="lpv-contact-sec">
          <div className="lpv-contact-sec-head">
            <IcoBuilding />
            <h3 className="lpv-contact-sec-title">Office Locations</h3>
          </div>
          {offices.length ? (
            <div className="lpv-contact-offices">
              {offices.map((loc, idx) => (
                <div key={idx} className="lpv-contact-office-card">
                  <div className="lpv-contact-office-name">{String(loc?.name || "Office").trim() || "Office"}</div>
                  <div className="lpv-contact-office-addr">{String(loc?.address || "").trim() || "—"}</div>
                  {String(loc?.phone || "").trim() ? (
                    <div className="lpv-contact-office-phone">
                      <IcoPhone />
                      <span>{String(loc.phone).trim()}</span>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted small">No office locations specified.</div>
          )}
        </section>

        <section className="lpv-contact-sec">
          <div className="lpv-contact-sec-head">
            <IcoChatSec />
            <h3 className="lpv-contact-sec-title">Communication Channels</h3>
          </div>
          <div className="lpv-contact-tiles">
            {[
              { key: "callUs", label: "Call Us", icon: <IcoTilePhone />, on: !!ch.callUs },
              { key: "email", label: "Email", icon: <IcoTileEmail />, on: !!ch.email },
              { key: "liveChat", label: "Live Chat", icon: <IcoTileChat />, on: !!ch.liveChat },
              { key: "scheduleCall", label: "Schedule Call", icon: <IcoTileCalendar />, on: !!ch.scheduleCall },
            ].map((t) => (
              <div key={t.key} className={`lpv-contact-tile ${t.on ? "is-on" : ""}`}>
                {t.icon}
                <div className="lpv-contact-tile-label">{t.label}</div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <aside className="lpv-contact-aside">
        <div className="lpv-contact-side-card">
          <h3 className="lpv-contact-side-title">Quick Actions</h3>
          <div className="lpv-contact-qa-stack">
            <button type="button" className="lpv-contact-qa lpv-contact-qa--primary" onClick={() => toast.info("Request quote is coming soon.")}>
              <IcoMsg />
              Request Quote
            </button>
            {phone ? (
              <a className="lpv-contact-qa lpv-contact-qa--outline" href={`tel:${phone.replace(/\s/g, "")}`}>
                <IcoPhone />
                Call Now
              </a>
            ) : (
              <button type="button" className="lpv-contact-qa lpv-contact-qa--outline" disabled>
                <IcoPhone />
                Call Now
              </button>
            )}
            {email ? (
              <a className="lpv-contact-qa lpv-contact-qa--outline" href={`mailto:${email}`}>
                <IcoEmail />
                Send Email
              </a>
            ) : (
              <button type="button" className="lpv-contact-qa lpv-contact-qa--outline" disabled>
                <IcoEmail />
                Send Email
              </button>
            )}
            {brochureUrl ? (
              <a className="lpv-contact-qa lpv-contact-qa--outline" href={brochureUrl} target="_blank" rel="noreferrer">
                <IcoDownload />
                Company Profile
              </a>
            ) : (
              <button type="button" className="lpv-contact-qa lpv-contact-qa--outline" onClick={() => toast.info("No company profile document on file.")}>
                <IcoDownload />
                Company Profile
              </button>
            )}
            <button type="button" className="lpv-contact-qa lpv-contact-qa--outline" onClick={() => toast.info("Shortlist is coming soon.")}>
              <IcoBookmark />
              Save to Shortlist
            </button>
            <button type="button" className="lpv-contact-qa lpv-contact-qa--outline" onClick={() => toast.info("Compare is coming soon.")}>
              <IcoCompare />
              Add to Compare
            </button>
          </div>
        </div>

        <div className="lpv-contact-side-card lpv-contact-side-card--hours">
          <div className="lpv-contact-hours-head">
            <IcoClock />
            <h3 className="lpv-contact-side-title mb-0">Business Hours</h3>
          </div>
          <ul className="lpv-contact-hours">
            <li>
              <span>Monday - Friday</span>
              <strong>{String(bh.monFri || "").trim() || "—"}</strong>
            </li>
            <li>
              <span>Saturday</span>
              <strong>{String(bh.saturday || "").trim() || "—"}</strong>
            </li>
            <li>
              <span>Sunday</span>
              <strong>{String(bh.sunday || "").trim() || "—"}</strong>
            </li>
          </ul>
          {cd.customerSupport247 ? (
            <div className="lpv-contact-247">
              <span aria-hidden>✓</span>
              24/7 Support Available
            </div>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

export default function LogisticsProviderProfileView() {
  const navigate = useNavigate();
  const { companyId: companyIdParam } = useParams();
  const location = useLocation();
  const publicCompanyId = useMemo(() => {
    if (companyIdParam == null || companyIdParam === "") return null;
    const id = parseInt(String(companyIdParam), 10);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [companyIdParam]);
  const isPublicBrowse = publicCompanyId != null;

  const backReturnTo =
    typeof location.state?.returnTo === "string" && location.state.returnTo.startsWith("/")
      ? location.state.returnTo
      : null;
  const backLabel =
    typeof location.state?.returnLabel === "string" && location.state.returnLabel.trim()
      ? location.state.returnLabel.trim()
      : isPublicBrowse
        ? "← Back to Search"
        : "← Back";

  const handleBack = useCallback(() => {
    if (backReturnTo) {
      navigate(backReturnTo);
      return;
    }
    if (isPublicBrowse) {
      navigate("/logistics-providers");
      return;
    }
    navigate(-1);
  }, [backReturnTo, isPublicBrowse, navigate]);

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [company, setCompany] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (location.hash === "#contact") {
      setActiveTab("contact");
    }
  }, [location.hash, publicCompanyId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isPublicBrowse) {
        const logRes = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.PUBLIC_PROFILE(publicCompanyId));
        if (logRes?.success && logRes?.data) {
          const row = logRes.data;
          setCompany({
            company_name: row.company_name,
            company_description: row.company_description,
            verification_status: row.verification_status,
            address: row.address,
          });
          setProfile(normalizeLogisticsPayload(row));
        } else {
          setCompany(null);
          setProfile(null);
          toast.error("Logistics provider not found");
          navigate("/logistics-providers", { replace: true });
        }
        return;
      }

      const user = StorageService.getUser();
      if (!user) {
        navigate("/login");
        return;
      }
      const [myRes, logRes] = await Promise.all([
        CommonService.get("/company/my-profile"),
        CommonService.get("/company/logistics/my-profile"),
      ]);
      const c = myRes?.data ?? myRes;
      setCompany(c);
      if (logRes?.success && logRes?.data) {
        setProfile(normalizeLogisticsPayload(logRes.data));
      } else {
        setProfile(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to load profile");
      setProfile(null);
      if (isPublicBrowse) {
        navigate("/logistics-providers", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, isPublicBrowse, publicCompanyId]);

  useEffect(() => {
    load();
  }, [load]);

  const bi = profile?.basic_information || {};
  const companyName = String(bi.companyName || bi.company_name || company?.company_name || "Logistics provider").trim();
  const companyType = String(bi.companyType || bi.company_type || "—").trim();
  const years = bi.yearsInBusiness ?? bi.years_in_business;
  const yearsLabel = years !== "" && years != null && !Number.isNaN(Number(years)) ? `${years} Years` : "—";
  const hq = String(bi.headquartersLocation || bi.headquarters_location || "—").trim();
  const hqShort = hq.length > 42 ? `${hq.slice(0, 40)}…` : hq;
  const about = String(bi.aboutCompany || bi.about_company || "").trim();
  const logoPath = bi.companyLogo || bi.company_logo || "";
  const responseTime = String(bi.responseTime || bi.response_time || "").trim();
  const totalShipments = bi.completedShipments ?? bi.completed_shipments ?? "";
  const avgRating = bi.averageRating ?? bi.average_rating ?? "";
  const reviewsCount = bi.reviewsCount ?? bi.reviews_count ?? "";
  const repeatCustomers = bi.repeatCustomersPercent ?? bi.repeat_customers_percent ?? "";
  const isVerified = !!(bi.verifiedProvider ?? bi.verified_provider);
  const activeOnPlatform = !!(bi.activeOnPlatform ?? bi.active_on_platform);
  const modes = profile?.transport_modes?.modes || [];
  const modePills = useMemo(
    () =>
      modes
        .map((id) => {
          const key = String(id).toLowerCase();
          return MODE_META[key] ? { id: key, ...MODE_META[key] } : { id: key, label: String(id), tone: "multi" };
        })
        .filter(Boolean),
    [modes]
  );

  const initials = useMemo(() => {
    const parts = companyName.split(/\s+/).filter(Boolean);
    const a = (parts[0] || "?")[0] || "?";
    const b = (parts[1] || "")[0] || "";
    return (a + b).toUpperCase();
  }, [companyName]);

  if (loading) {
    return (
      <div className="lpv-page">
        <div className="lpv-loading">Loading profile…</div>
      </div>
    );
  }

  return (
    <div className="lpv-page">
      <div className="lpv-topbar">
        <div className="lpv-breadcrumbs">
          <button
            type="button"
            className="lpv-back"
            onClick={handleBack}
          >
            {backLabel}
          </button>
          <span className="lpv-crumb-sep">›</span>
          <span className="lpv-crumb">Logistics Providers</span>
          <span className="lpv-crumb-sep">›</span>
          <span className="lpv-crumb lpv-crumb-active">{companyName}</span>
        </div>
        <div className="lpv-topbar-actions">
          {!isPublicBrowse ? (
            <Link to="/logistics-profile/edit" className="btn btn-outline-primary btn-sm">
              Edit profile
            </Link>
          ) : null}
        </div>
      </div>

      {!profile ? (
        <div className="lpv-empty card shadow-sm border-0 rounded-3 p-5 text-center">
          <h2 className="h5 mb-2">No logistics profile yet</h2>
          <p className="text-muted mb-4">Complete your logistics provider details to show them here.</p>
          <Link to="/logistics-profile/edit" className="btn btn-primary">
            Create / edit profile
          </Link>
        </div>
      ) : (
        <>
          <section className="lpv-hero card border-0 shadow-sm">
            <div className="lpv-hero-main">
              <div className="lpv-hero-brand">
                <div className="lpv-logo-wrap">
                  {logoPath ? (
                    <img className="lpv-logo" src={resolvePublicFileUrl(logoPath)} alt="" />
                  ) : (
                    <span className="lpv-logo-fallback">{initials}</span>
                  )}
                </div>
                <div>
                  <div className="lpv-title-row">
                    <h1 className="lpv-company-name">{companyName}</h1>
                    {isVerified && <span className="lpv-badge lpv-badge-verified">Verified Provider</span>}
                  </div>
                  <div className="lpv-mode-row">
                    {modePills.length ? (
                      modePills.map((m) => (
                        <span key={m.id} className={`lpv-mode-pill lpv-mode-pill--${m.tone}`}>
                          <span className="lpv-mode-pill-icon" aria-hidden>
                            {m.icon || "•"}
                          </span>
                          {m.label}
                        </span>
                      ))
                    ) : (
                      <span className="text-muted small">No transport modes selected</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="lpv-hero-meta">
                <div className="lpv-meta-item">
                  <span className="lpv-meta-icon" aria-hidden>
                    🏢
                  </span>
                  <div>
                    <div className="lpv-meta-label">Company Type</div>
                    <div className="lpv-meta-value">{companyType || "—"}</div>
                  </div>
                </div>
                <div className="lpv-meta-item">
                  <span className="lpv-meta-icon" aria-hidden>
                    📍
                  </span>
                  <div>
                    <div className="lpv-meta-label">Headquarters</div>
                    <div className="lpv-meta-value" title={hq}>
                      {hqShort}
                    </div>
                  </div>
                </div>
                <div className="lpv-meta-item">
                  <span className="lpv-meta-icon" aria-hidden>
                    ⏱
                  </span>
                  <div>
                    <div className="lpv-meta-label">Experience</div>
                    <div className="lpv-meta-value">{yearsLabel}</div>
                  </div>
                </div>
                <div className="lpv-meta-item">
                  <span className="lpv-meta-icon" aria-hidden>
                    ⚡
                  </span>
                  <div>
                    <div className="lpv-meta-label">Response time</div>
                    <div className="lpv-meta-value">{responseTime || "—"}</div>
                  </div>
                </div>
              </div>

              <div className="lpv-hero-rating">
                {avgRating ? (
                  <>
                    <div className="lpv-stars" aria-hidden>
                      ★★★★★
                    </div>
                    <div>
                      <strong>{avgRating}</strong>
                      <span className="text-muted small ms-1">/ 5.0</span>
                      <div className="small text-muted">{reviewsCount ? `(${reviewsCount} reviews)` : ""}</div>
                    </div>
                  </>
                ) : null}

                {totalShipments ? (
                  <div className="lpv-shipments">
                    <div className="lpv-shipments-value">{totalShipments}</div>
                    <div className="small text-muted">Completed shipments</div>
                  </div>
                ) : null}
                {activeOnPlatform ? <span className="lpv-active-pill">Active on Troconet</span> : null}
              </div>
            </div>

            <div className="lpv-hero-actions">
              <div className="lpv-icon-actions">
                <button type="button" className="lpv-icon-btn" title="Save" onClick={() => toast.info("Saved list coming soon.")}>
                  ♡
                </button>
                <button type="button" className="lpv-icon-btn" title="Stats" onClick={() => toast.info("Analytics coming soon.")}>
                  📊
                </button>
                <button type="button" className="lpv-icon-btn" title="Download" onClick={() => toast.info("Export coming soon.")}>
                  ⬇
                </button>
              </div>
            </div>
          </section>

          <nav className="lpv-tabs" aria-label="Profile sections">
            {VIEW_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`lpv-tab ${activeTab === t.key ? "active" : ""}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </nav>

          <div className={`lpv-layout${activeTab === "contact" ? " lpv-layout--contact-full" : ""}`}>
            <div className="lpv-main">
              {activeTab === "overview" && (
                <>
                  <section className="lpv-panel card border-0 shadow-sm">
                    <div className="lpv-panel-head">
                      <h2 className="lpv-panel-title">About the Company</h2>
                      <div className="lpv-panel-subtitle">Company overview and core capabilities</div>
                    </div>
                    <p className="lpv-prose">{about || "No company description has been added yet."}</p>

                    <div className="lpv-overview-grid mt-3">
                      <div className="lpv-overview-block">
                        <div className="lpv-overview-label">
                          <span className="lpv-overview-ico" aria-hidden>
                            🧩
                          </span>
                          Core Specialization
                        </div>
                        <div className="lpv-tag-row">
                          {(profile.capabilities?.coreSpecialization || []).length ? (
                            profile.capabilities.coreSpecialization.map((x) => (
                              <span key={x} className="lpv-tag lpv-tag--blue">
                                {x}
                              </span>
                            ))
                          ) : (
                            <span className="text-muted small">Not specified</span>
                          )}
                        </div>
                      </div>

                      <div className="lpv-overview-block">
                        <div className="lpv-overview-label">
                          <span className="lpv-overview-ico" aria-hidden>
                            ⚙
                          </span>
                          Operating Model
                        </div>
                        <div className="lpv-tag-row">
                          {profile.capabilities?.operatingModel || bi.operatingModel ? (
                            <span className="lpv-tag lpv-tag--green">
                              {profile.capabilities.operatingModel || bi.operatingModel}
                            </span>
                          ) : (
                            <span className="text-muted small">Not specified</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="lpv-overview-grid mt-3">
                      <div className="lpv-overview-block">
                        <div className="lpv-overview-label">
                          <span className="lpv-overview-ico" aria-hidden>
                            🌐
                          </span>
                          Languages Supported
                        </div>
                        <div className="lpv-overview-value">
                          {(profile.contact_details?.languagesSupported || []).join(", ") || "—"}
                        </div>
                      </div>

                      <div className="lpv-overview-block">
                        <div className="lpv-overview-label">
                          <span className="lpv-overview-ico" aria-hidden>
                            🕘
                          </span>
                          Support Availability
                        </div>
                        <div className="lpv-support-pill">
                          {profile.contact_details?.customerSupport247 ? (
                            <>
                              <span className="lpv-support-check" aria-hidden>
                                ✓
                              </span>
                              24/7 Support Available
                            </>
                          ) : (
                            <span className="text-muted">Not specified</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="lpv-panel card border-0 shadow-sm mt-3">
                    <div className="lpv-panel-head">
                      <h2 className="lpv-panel-title">Industries &amp; Cargo Served</h2>
                      <div className="lpv-panel-subtitle">Sectors and cargo types we handle</div>
                    </div>
                    <div className="lpv-overview-label mt-2">
                      <span className="lpv-overview-ico" aria-hidden>
                        🏭
                      </span>
                      Key Industries
                    </div>
                    <div className="lpv-tag-row">
                      {(profile.capabilities?.industriesServed || []).map((x) => (
                        <span key={x} className="lpv-tag lpv-tag--indigo">
                          {x}
                        </span>
                      ))}
                      {!(profile.capabilities?.industriesServed || []).length && (
                        <span className="text-muted small">Not specified</span>
                      )}
                    </div>

                    <div className="lpv-overview-label mt-3">
                      <span className="lpv-overview-ico" aria-hidden>
                        📦
                      </span>
                      Preferred Shipment Types
                    </div>
                    <div className="lpv-tag-row">
                      {(profile.transport_modes?.preferredShipmentTypes || []).map((x) => (
                        <span key={x} className="lpv-tag lpv-tag--purple">
                          {x}
                        </span>
                      ))}
                      {!(profile.transport_modes?.preferredShipmentTypes || []).length && (
                        <span className="text-muted small">Not specified</span>
                      )}
                    </div>

                    <div className="lpv-overview-label mt-3">
                      <span className="lpv-overview-ico" aria-hidden>
                        🧪
                      </span>
                      Special Cargo Capabilities
                    </div>
                    <div className="lpv-cargo-pills">
                      {SPECIAL_CARGO_OPTIONS.map((label) => {
                        const selected = (profile.capabilities?.specialCargo || []).includes(label);
                        if (!selected) return null;
                        return (
                          <span key={label} className="lpv-cargo-pill">
                            <span className="lpv-support-check" aria-hidden>
                              ✓
                            </span>
                            {label}
                          </span>
                        );
                      })}
                      {!(profile.capabilities?.specialCargo || []).length ? (
                        <span className="text-muted small">Not specified</span>
                      ) : null}
                    </div>
                  </section>
                </>
              )}

              {activeTab === "capabilities" && (
                <section className="lpv-panel card border-0 shadow-sm">
                  <div className="lpv-panel-head">
                    <h2 className="lpv-panel-title">Operational Capabilities by Transport Mode</h2>
                    <div className="lpv-panel-subtitle">
                      Detailed breakdown of services and capabilities across different transport modes
                    </div>
                  </div>

                  <div className="lpv-mode-cards">
                    {(profile.transport_modes?.modes || [])
                      .map((id) => String(id || "").toLowerCase())
                      .filter(Boolean)
                      .map((modeKey) => {
                        const meta = MODE_META[modeKey] || { label: modeKey, tone: "multi", icon: "•" };
                        const obm = profile.capabilities?.operationalByMode?.[modeKey] || {};

                        const serviceTypes = Array.isArray(obm.serviceTypes) ? obm.serviceTypes : [];
                        const containerTypes = Array.isArray(obm.containerTypes) ? obm.containerTypes : [];

                        const rows = (() => {
                          if (modeKey === "road") {
                            return [
                              { label: "Service Types", value: serviceTypes.length ? serviceTypes.join(", ") : "—" },
                              { label: "Coverage", value: obm.coverage || "—" },
                              { label: "Special Cargo", value: obm.specialCargo || "—" },
                              { label: "GPS Tracking", value: obm.gpsTracking ? "Enabled" : "—", pill: !!obm.gpsTracking },
                            ];
                          }
                          if (modeKey === "ocean") {
                            return [
                              { label: "Container Types", value: containerTypes.length ? containerTypes.join(", ") : "—" },
                              { label: "Service Model", value: obm.serviceModel || "—" },
                              { label: "Special Cargo", value: obm.specialCargo || "—" },
                              {
                                label: "Customer Support",
                                value: obm.customerSupport ? "Available" : "—",
                                pill: !!obm.customerSupport,
                              },
                            ];
                          }
                          if (modeKey === "air") {
                            return [
                              { label: "Service Types", value: serviceTypes.length ? serviceTypes.join(", ") : "—" },
                              { label: "Cargo Types", value: obm.cargoTypes || "—" },
                              { label: "Airlines", value: obm.airlines || "—" },
                              {
                                label: "Temperature Control",
                                value: obm.temperatureControl ? "Available" : "—",
                                pill: !!obm.temperatureControl,
                              },
                            ];
                          }
                          if (modeKey === "rail") {
                            return [
                              { label: "Service Types", value: serviceTypes.length ? serviceTypes.join(", ") : "—" },
                              { label: "Coverage", value: obm.coverage || "—" },
                              { label: "Special Cargo", value: obm.specialCargo || "—" },
                              { label: "Tracking", value: obm.tracking ? "Enabled" : "—", pill: !!obm.tracking },
                            ];
                          }
                          if (modeKey === "multimodal") {
                            return [
                              { label: "Service Types", value: serviceTypes.length ? serviceTypes.join(", ") : "—" },
                              { label: "Coverage", value: obm.coverage || "—" },
                              { label: "Special Handling", value: obm.specialCargo || "—" },
                              { label: "Tracking", value: obm.tracking ? "Enabled" : "—", pill: !!obm.tracking },
                            ];
                          }
                          return [
                            { label: "Service Types", value: serviceTypes.length ? serviceTypes.join(", ") : "—" },
                            { label: "Coverage", value: obm.coverage || "—" },
                            { label: "Notes", value: obm.notes || "—" },
                            { label: "Tracking", value: obm.tracking ? "Enabled" : "—", pill: !!obm.tracking },
                          ];
                        })();

                        return (
                          <div key={modeKey} className={`lpv-mode-card lpv-mode-card--${meta.tone}`}>
                            <div className="lpv-mode-card-head">
                              <div className="lpv-mode-card-title">
                                <span className="lpv-mode-card-ico" aria-hidden>
                                  {meta.icon}
                                </span>
                                {meta.label}
                              </div>
                              <span className="lpv-mode-active">Active</span>
                            </div>

                            <div className="lpv-mode-grid">
                              {rows.map((r) => (
                                <div key={r.label} className="lpv-mode-cell">
                                  <div className="lpv-mode-label">{r.label}</div>
                                  <div className={`lpv-mode-value ${r.pill ? "lpv-mode-value--pill" : ""}`}>
                                    {r.pill ? (
                                      <>
                                        <span className="lpv-support-check" aria-hidden>
                                          ✓
                                        </span>
                                        {r.value}
                                      </>
                                    ) : (
                                      r.value
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                  </div>

                  <div className="lpv-divider" />

                  <div className="lpv-panel-head">
                    <h2 className="lpv-panel-title">Additional Service Capabilities</h2>
                    <div className="lpv-panel-subtitle">Value-added services and support offerings</div>
                  </div>

                  {(() => {
                    const saved = Array.isArray(profile?.capabilities?.additionalServiceCapabilities)
                      ? profile.capabilities.additionalServiceCapabilities
                      : [];
                    const savedSet = new Set(saved.map((x) => String(x || "").trim().toLowerCase()).filter(Boolean));
                    const tiles = [
                      {
                        key: "Warehousing",
                        tone: "blue",
                        title: "Warehousing",
                        bullets: ["Storage facilities", "Inventory management", "Pick & pack services"],
                      },
                      {
                        key: "Customs & Compliance",
                        tone: "mint",
                        title: "Customs & Compliance",
                        bullets: ["Customs clearance", "Documentation support", "Compliance advisory"],
                      },
                      {
                        key: "Insurance & Risk",
                        tone: "purple",
                        title: "Insurance & Risk",
                        bullets: ["Cargo insurance", "Risk assessment", "Claims support"],
                      },
                    ].filter((t) => savedSet.has(String(t.key).toLowerCase()));

                    if (!tiles.length) {
                      return <div className="text-muted small">Not specified</div>;
                    }

                    return (
                      <div className="lpv-service-tiles">
                        {tiles.map((t) => (
                          <div key={t.key} className={`lpv-service-tile lpv-service-tile--${t.tone}`}>
                            <div className="lpv-service-title">{t.title}</div>
                            <ul className="lpv-service-list">
                              {t.bullets.map((b) => (
                                <li key={b}>
                                  <span className="lpv-service-dot" aria-hidden />
                                  {b}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </section>
              )}

              {activeTab === "fleet" && (
                <section className="lpv-panel card border-0 shadow-sm">
                  <div className="lpv-panel-head">
                    <h2 className="lpv-panel-title">Fleet &amp; Asset Overview</h2>
                    <div className="lpv-panel-subtitle">Comprehensive breakdown of owned and managed assets</div>
                  </div>

                  {(() => {
                    const ov = profile.fleet_assets?.overview || {};
                    const ws = profile.fleet_assets?.warehousesStorage || {};
                    const rb = profile.fleet_assets?.roadFleetBreakdown || {};
                    const tt = profile.fleet_assets?.technologyTracking || {};

                    const totalTrucks = ov.totalTrucks || "";
                    const trucksOwned = ov.trucksOwned || "";
                    const trucksLeased = ov.trucksLeased || "";
                    const trucksNetwork = ov.trucksNetwork || "";

                    const containersTotal = ov.containersTotal || "";
                    const containers20ft = ov.containers20ft || "";
                    const containers40ft = ov.containers40ft || "";
                    const containersReefer = ov.containersReefer || "";

                    const railWagonsTotal = ov.railWagonsTotal || "";
                    const railCovered = ov.railCovered || "";
                    const railOpen = ov.railOpen || "";
                    const railSpecial = ov.railSpecial || "";

                    const warehousesTotal = ws.totalWarehouses || ov.warehousesTotal || "";
                    const totalArea = ws.totalAreaSqFt || "";
                    const coldStorage = ws.coldStorageUnits || "";
                    const bonded = ws.bondedWarehouses || "";

                    const gps = !!tt.gpsFleetTracking;
                    const iot = !!tt.iotSensors;
                    const fms = !!tt.fleetManagementSystem;
                    const gpsCoverage = tt.gpsCoveragePercent || tt.gpsCoverage || "";
                    const iotNote = tt.iotNote || "";
                    const fmsNote = tt.fmsNote || "";

                    return (
                      <>
                        <div className="lpv-fleet-summary">
                          <div className="lpv-fleet-card lpv-fleet-card--amber">
                            <div className="lpv-fleet-card-head">
                              <span className="lpv-fleet-ico" aria-hidden>
                                🚚
                              </span>
                              <div className="lpv-fleet-card-title">Total Trucks</div>
                            </div>
                            <div className="lpv-fleet-card-value">{totalTrucks || "—"}</div>
                            <div className="lpv-fleet-mini">
                              <div>Owned:</div>
                              <strong>{trucksOwned || "—"}</strong>
                              <div>Leased:</div>
                              <strong>{trucksLeased || "—"}</strong>
                              <div>Network:</div>
                              <strong>{trucksNetwork || "—"}</strong>
                            </div>
                          </div>

                          <div className="lpv-fleet-card lpv-fleet-card--sky">
                            <div className="lpv-fleet-card-head">
                              <span className="lpv-fleet-ico" aria-hidden>
                                📦
                              </span>
                              <div className="lpv-fleet-card-title">Containers</div>
                            </div>
                            <div className="lpv-fleet-card-value">{containersTotal || "—"}</div>
                            <div className="lpv-fleet-mini">
                              <div>20ft:</div>
                              <strong>{containers20ft || "—"}</strong>
                              <div>40ft:</div>
                              <strong>{containers40ft || "—"}</strong>
                              <div>Reefer:</div>
                              <strong>{containersReefer || "—"}</strong>
                            </div>
                          </div>

                          <div className="lpv-fleet-card lpv-fleet-card--lavender">
                            <div className="lpv-fleet-card-head">
                              <span className="lpv-fleet-ico" aria-hidden>
                                🚆
                              </span>
                              <div className="lpv-fleet-card-title">Rail Wagons</div>
                            </div>
                            <div className="lpv-fleet-card-value">{railWagonsTotal || "—"}</div>
                            <div className="lpv-fleet-mini">
                              <div>Covered:</div>
                              <strong>{railCovered || "—"}</strong>
                              <div>Open:</div>
                              <strong>{railOpen || "—"}</strong>
                              <div>Special:</div>
                              <strong>{railSpecial || "—"}</strong>
                            </div>
                          </div>

                          <div className="lpv-fleet-card lpv-fleet-card--slate">
                            <div className="lpv-fleet-card-head">
                              <span className="lpv-fleet-ico" aria-hidden>
                                🏬
                              </span>
                              <div className="lpv-fleet-card-title">Warehouses</div>
                            </div>
                            <div className="lpv-fleet-card-value">{warehousesTotal || "—"}</div>
                            <div className="lpv-fleet-mini">
                              <div>Total Area:</div>
                              <strong>{totalArea ? `${totalArea} sq ft` : "—"}</strong>
                              <div>Cold Storage:</div>
                              <strong>{coldStorage || "—"}</strong>
                              <div>Bonded:</div>
                              <strong>{bonded || "—"}</strong>
                            </div>
                          </div>
                        </div>

                        <div className="lpv-divider" />

                        <div className="lpv-panel-head">
                          <h2 className="lpv-panel-title">Road Fleet Breakdown</h2>
                        </div>

                        <div className="lpv-road-grid">
                          {[
                            ["Container Trucks", rb.containerTrucks],
                            ["Open Body Trucks", rb.openBodyTrucks],
                            ["Refrigerated Trucks", rb.refrigeratedTrucks],
                            ["Tankers", rb.tankers],
                            ["Flatbed Trailers", rb.flatbedTrailers],
                            ["Low Bed Trailers", rb.lowBedTrailers],
                            ["Multi-Axle Vehicles", rb.multiAxleVehicles],
                            ["Light Commercial", rb.lightCommercial],
                          ].map(([label, val]) => (
                            <div key={label} className="lpv-road-card">
                              <div className="lpv-road-title">{label}</div>
                              <div className="lpv-road-value">{val !== "" && val != null ? val : "—"}</div>
                              <div className="lpv-road-sub">Units Available</div>
                            </div>
                          ))}
                        </div>

                        <div className="lpv-divider" />

                        <div className="lpv-panel-head">
                          <h2 className="lpv-panel-title">Technology &amp; Tracking Capabilities</h2>
                        </div>

                        {(() => {
                          const showGps = gps || String(gpsCoverage || "").trim();
                          const showIot = iot || String(iotNote || "").trim();
                          const showFms = fms || String(fmsNote || "").trim();

                          const cards = [];
                          if (showGps) {
                            const cov = String(gpsCoverage || "").replace(/[^\d]/g, "");
                            cards.push(
                              <div key="gps" className={`lpv-tech-card lpv-tech-card--gps ${gps ? "active" : ""}`}>
                                <div className="lpv-tech-title">GPS Fleet Tracking</div>
                                <div className="lpv-tech-sub">Real-time visibility across 100% of owned fleet</div>
                                {gps && cov ? <span className="lpv-tech-pill lpv-tech-pill--gps">{cov}% Coverage</span> : null}
                              </div>
                            );
                          }
                          if (showIot) {
                            cards.push(
                              <div key="iot" className={`lpv-tech-card lpv-tech-card--iot ${iot ? "active" : ""}`}>
                                <div className="lpv-tech-title">IoT Sensors</div>
                                <div className="lpv-tech-sub">Temperature &amp; humidity monitoring for sensitive cargo</div>
                                {iot && String(iotNote || "").trim() ? (
                                  <span className="lpv-tech-pill lpv-tech-pill--iot">{String(iotNote).trim()}</span>
                                ) : null}
                              </div>
                            );
                          }
                          if (showFms) {
                            cards.push(
                              <div key="fms" className={`lpv-tech-card lpv-tech-card--fms ${fms ? "active" : ""}`}>
                                <div className="lpv-tech-title">Fleet Management System</div>
                                <div className="lpv-tech-sub">Automated maintenance scheduling and fuel monitoring</div>
                                {fms && String(fmsNote || "").trim() ? (
                                  <span className="lpv-tech-pill lpv-tech-pill--fms">{String(fmsNote).trim()}</span>
                                ) : null}
                              </div>
                            );
                          }

                          if (!cards.length) {
                            return <div className="text-muted small">Not specified</div>;
                          }

                          return <div className="lpv-tech-grid">{cards}</div>;
                        })()}
                      </>
                    );
                  })()}
                </section>
              )}

              {activeTab === "coverage" && (
                <section className="lpv-panel card border-0 shadow-sm">
                  <div className="lpv-cov-head">
                    <span className="lpv-cov-head-ico" aria-hidden>
                      🗺
                    </span>
                    <div>
                      <h2 className="lpv-panel-title" style={{ marginBottom: 4 }}>
                        Geographic Coverage &amp; Service Lanes
                      </h2>
                      <div className="lpv-panel-subtitle">
                        Countries, states, ports, airports, and major trade corridors we serve
                      </div>
                    </div>
                  </div>

                  <div className="lpv-cov-section">
                    <div className="lpv-cov-section-title">
                      <span className="lpv-cov-mini-ico" aria-hidden>
                        📍
                      </span>
                      Geographic Coverage
                    </div>
                    <div className="lpv-geo-grid">
                      {(profile.coverage_routes?.geographicCoverage || []).map((g, idx) => {
                        const label = String(g?.label || "").trim();
                        if (!label) return null;
                        return (
                          <div key={`${label}-${idx}`} className="lpv-geo-card">
                            <div className="lpv-geo-top">
                              <span className="lpv-geo-pin" aria-hidden>
                                📍
                              </span>
                              <div className="lpv-geo-name">{label}</div>
                            </div>
                            <div className="lpv-geo-check" aria-hidden>
                              ✓
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {!(profile.coverage_routes?.geographicCoverage || []).length ? (
                      <div className="text-muted small">Not specified</div>
                    ) : null}
                  </div>

                  <div className="lpv-cov-section">
                    <div className="lpv-cov-section-title">
                      <span className="lpv-cov-mini-ico" aria-hidden>
                        ⚓
                      </span>
                      Ports &amp; Maritime Terminals
                    </div>
                    <div className="lpv-port-grid">
                      {(profile.coverage_routes?.portsTerminals || []).map((p, idx) => {
                        const obj = typeof p === "string" ? { name: p } : p;
                        const name = String(obj?.name || "").trim();
                        if (!name) return null;
                        const portType = String(obj?.portType || obj?.type || "").trim();
                        const traffic = String(obj?.traffic || obj?.trafficLevel || "").trim();
                        return (
                          <div key={`${name}-${idx}`} className="lpv-port-card">
                            <div className="lpv-port-top">
                              <span className="lpv-port-ico" aria-hidden>
                                ⚓
                              </span>
                              <div className="lpv-port-name">{name}</div>
                            </div>
                            {portType ? <span className="lpv-cov-pill lpv-cov-pill--blue">{portType}</span> : null}
                            {traffic ? <div className="lpv-port-traffic">Traffic: {traffic}</div> : null}
                          </div>
                        );
                      })}
                    </div>
                    {!(profile.coverage_routes?.portsTerminals || []).length ? (
                      <div className="text-muted small">Not specified</div>
                    ) : null}
                  </div>

                  <div className="lpv-cov-section">
                    <div className="lpv-cov-section-title">
                      <span className="lpv-cov-mini-ico" aria-hidden>
                        ✈
                      </span>
                      Airport Network
                    </div>
                    <div className="lpv-air-grid">
                      {(profile.coverage_routes?.airportNetwork || []).map((a, idx) => {
                        const label = String(a?.label || "").trim();
                        if (!label) return null;
                        const tier = String(a?.tier || "").trim();
                        return (
                          <div key={`${label}-${idx}`} className="lpv-air-card">
                            <div className="lpv-air-top">
                              <span className="lpv-air-ico" aria-hidden>
                                ✈
                              </span>
                              <div className="lpv-air-name">{label}</div>
                            </div>
                            {tier ? <span className="lpv-cov-pill lpv-cov-pill--sky">{tier}</span> : null}
                          </div>
                        );
                      })}
                    </div>
                    {!(profile.coverage_routes?.airportNetwork || []).length ? (
                      <div className="text-muted small">Not specified</div>
                    ) : null}
                  </div>

                  <div className="lpv-cov-section">
                    <div className="lpv-cov-section-title">
                      <span className="lpv-cov-mini-ico" aria-hidden>
                        🧭
                      </span>
                      Major Trade Lanes &amp; Corridors
                    </div>
                    <div className="lpv-corridor-grid">
                      {(profile.coverage_routes?.corridors || []).map((c, idx) => {
                        const name = String(c?.name || "").trim();
                        if (!name) return null;
                        const modes = Array.isArray(c?.modes) ? c.modes : [];
                        const modeLabels = modes
                          .map((m) => {
                            const s = String(m || "").toLowerCase();
                            if (s === "ocean") return "Ocean";
                            if (s === "air") return "Air";
                            if (s === "road") return "Road";
                            if (s === "rail") return "Rail";
                            if (s === "multimodal") return "Multi-Modal";
                            return s ? s[0].toUpperCase() + s.slice(1) : "";
                          })
                          .filter(Boolean);
                        const traffic = String(c?.traffic || "").trim();
                        const freq = String(c?.frequency || "").trim();
                        return (
                          <div key={`${name}-${idx}`} className="lpv-corridor-card">
                            <div className="lpv-corridor-head">
                              <div className="lpv-corridor-title">
                                <span className="lpv-corridor-ico" aria-hidden>
                                  ✈
                                </span>
                                {name}
                              </div>
                              {traffic ? <span className="lpv-cov-pill lpv-cov-pill--mint">{traffic}</span> : null}
                            </div>
                            <div className="lpv-corridor-modes">
                              {modeLabels.map((m) => (
                                <span key={m} className="lpv-mode-chip-lite">
                                  {m}
                                </span>
                              ))}
                            </div>
                            {freq ? (
                              <div className="lpv-corridor-freq">
                                <span aria-hidden>🕒</span>
                                Frequency: {freq}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                    {!(profile.coverage_routes?.corridors || []).length ? (
                      <div className="text-muted small">Not specified</div>
                    ) : null}
                  </div>
                </section>
              )}

              {activeTab === "certifications" && (
                <section className="lpv-panel card border-0 shadow-sm">
                  <div className="lpv-cert-page-head">
                    <span className="lpv-cert-page-ico" aria-hidden>
                      ✓
                    </span>
                    <div>
                      <h2 className="lpv-panel-title" style={{ marginBottom: 4 }}>
                        Certifications, Compliance &amp; Legal
                      </h2>
                      <div className="lpv-panel-subtitle">
                        Industry certifications, regulatory compliance, and legal registrations
                      </div>
                    </div>
                  </div>

                  <div className="lpv-divider" />

                  <div className="lpv-cert-block">
                    <div className="lpv-cert-block-title">
                      <span className="lpv-cert-block-ico" aria-hidden>
                        📄
                      </span>
                      Company Registrations
                    </div>

                    <div className="lpv-cert-grid">
                      {(() => {
                        const regs = profile.certifications?.registrations || {};
                        const items = [
                          { title: "Company Registration", fieldLabel: "CIN", value: regs.cinNumber || regs.cin_number },
                          { title: "GST Registration", fieldLabel: "GSTIN", value: regs.gstin || regs.GSTIN },
                          { title: "PAN Number", fieldLabel: "PAN", value: regs.pan || regs.PAN },
                          { title: "Import Export Code", fieldLabel: "IEC", value: regs.iec || regs.importExportCode || regs.import_export_code },
                          { title: "MSME Registration", fieldLabel: "MSME", value: regs.msme || regs.msmeRegistration || regs.msme_registration },
                          { title: "TAN Number", fieldLabel: "TAN", value: regs.tan || regs.tanNumber || regs.tan_number },
                        ];

                        return items.map((it) => {
                          const val = String(it.value || "").trim();
                          return (
                          <div key={it.title} className="lpv-reg-card">
                            <div className="lpv-reg-head">
                              <span className="lpv-reg-ico" aria-hidden>
                                📄
                              </span>
                              <div className="lpv-reg-title">{it.title}</div>
                              {val ? (
                                <span className="lpv-reg-check" aria-hidden>
                                  ✓
                                </span>
                              ) : (
                                <span />
                              )}
                            </div>
                            <div className="lpv-reg-body">
                              <div className="lpv-reg-k">
                                {it.fieldLabel}: <span className="lpv-reg-v">{val || "—"}</span>
                              </div>
                            </div>
                          </div>
                          );
                        });
                      })()}
                    </div>
                  </div>

                  <div className="lpv-divider" />

                  <div className="lpv-cert-block">
                    <div className="lpv-cert-block-title">
                      <span className="lpv-cert-block-ico lpv-cert-block-ico--purple" aria-hidden>
                        🏅
                      </span>
                      Industry Certifications
                    </div>

                    <div className="lpv-cert-grid">
                      {(profile.certifications?.industryCertifications || []).map((c, i) => {
                        const name = String(c?.name || "Certification").trim();
                        const issuer = String(c?.issuer || "").trim();
                        const validity = String(c?.validity || "").trim();
                        const subtitle =
                          String(c?.category || c?.type || c?.scope || "").trim() ||
                          (() => {
                            const n = name;
                            const m =
                              n.match(/\s[-–]\s(.+)$/) ||
                              n.match(/\s:\s(.+)$/) ||
                              n.match(/,\s(.+)$/);
                            return m ? String(m[1] || "").trim() : "";
                          })();
                        const vLower = validity.toLowerCase();
                        const isLifetime = vLower.includes("life");
                        const activeLabel = isLifetime ? "Lifetime" : "Active";

                        const icon = (() => {
                          const n = name.toLowerCase();
                          if (n.includes("iso 14001")) return "🌿";
                          if (n.includes("iso 45001")) return "🦺";
                          if (n.includes("iso 9001")) return "🏅";
                          if (n.includes("aeo")) return "🛃";
                          if (n.includes("iata")) return "✈";
                          if (n.includes("fiata")) return "🌍";
                          return "📜";
                        })();

                        return (
                          <div key={`${name}-${i}`} className="lpv-ind-card">
                            <div className="lpv-ind-top">
                              <div className="lpv-ind-ico">{icon}</div>
                              <div className="lpv-ind-titles">
                                <div className="lpv-ind-name">{name}</div>
                                {subtitle ? <div className="lpv-ind-sub">{subtitle}</div> : null}
                              </div>
                            </div>

                            <div className="lpv-ind-meta">
                              <div>
                                <span className="lpv-ind-meta-k">Issued by:</span>{" "}
                                <span className="lpv-ind-meta-v">{issuer || "—"}</span>
                              </div>
                              <div className="mt-1">
                                <span className="lpv-ind-meta-k">Valid until:</span>{" "}
                                <span className="lpv-ind-meta-v">{validity || "—"}</span>
                              </div>
                            </div>

                            <div className="lpv-ind-foot">
                              <span className="lpv-ind-active">
                                <span className="lpv-ind-active-dot" aria-hidden />
                                {activeLabel}
                              </span>
                              {c?.attachmentUrl ? (
                                <a className="lpv-ind-link" href={resolvePublicFileUrl(c.attachmentUrl)} target="_blank" rel="noreferrer">
                                  View
                                </a>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {!(profile.certifications?.industryCertifications || []).length ? (
                      <div className="text-muted small">Not specified</div>
                    ) : null}
                  </div>
                </section>
              )}

              {activeTab === "performance" && renderLogisticsPerformanceTab(profile)}

              {activeTab === "reviews" && <LogisticsReviewsTab profile={profile} />}

              {activeTab === "contact" && (
                <section className="lpv-panel card border-0 shadow-sm lpv-contact-panel">
                  <LogisticsContactTab profile={profile} company={company} />
                </section>
              )}
            </div>

            {activeTab !== "contact" && (
            <aside className="lpv-aside">
              <div className="lpv-side-card card border-0 shadow-sm">
                <h3 className="lpv-side-title">Quick Stats</h3>
                <ul className="lpv-stat-list">
                  <li>
                    <span>Total shipments</span>
                    <strong>{totalShipments || "—"}</strong>
                  </li>
                  <li>
                    <span>Average rating</span>
                    <strong>{avgRating || "—"}</strong>
                  </li>
                  <li>
                    <span>Response time</span>
                    <strong>{responseTime || "—"}</strong>
                  </li>
                  <li>
                    <span>Years active</span>
                    <strong>{yearsLabel}</strong>
                  </li>
                  <li>
                    <span>Repeat customers</span>
                    <strong>{repeatCustomers ? `${repeatCustomers}%` : "—"}</strong>
                  </li>
                </ul>
              </div>
              <div className="lpv-side-card card border-0 shadow-sm mt-3">
                <h3 className="lpv-side-title">Service highlights</h3>
                <ul className="lpv-highlight-list mb-0">
                  {(
                    (profile?.capabilities?.serviceHighlights || []).length
                      ? profile.capabilities.serviceHighlights
                      : ["Real-time Tracking", "Customs Clearance", "Insurance Coverage", "Door-to-Door Service", "Warehousing Support", "ESG Compliance"]
                  ).map((x) => {
                    const k = String(x || "").toLowerCase();
                    const icon =
                      k.includes("track") ? "📍" : k.includes("custom") ? "🧾" : k.includes("insur") ? "🛡" : k.includes("door") ? "🚪" : k.includes("ware") ? "🏬" : k.includes("esg") ? "🌿" : "✓";
                    const subtitle =
                      k.includes("track")
                        ? "GPS-enabled fleet monitoring"
                        : k.includes("custom")
                          ? "End-to-end documentation"
                          : k.includes("insur")
                            ? "Comprehensive cargo protection"
                            : k.includes("door")
                              ? "First & last mile"
                              : k.includes("ware")
                                ? "Storage & distribution facilities"
                                : k.includes("esg")
                                  ? "Sustainable logistics practices"
                                  : "";
                    return (
                      <li key={x} className="lpv-highlight-item">
                        <span className="lpv-highlight-icon" aria-hidden>
                          {icon}
                        </span>
                        <div>
                          <div className="lpv-highlight-title">{x}</div>
                          {subtitle ? <div className="lpv-highlight-sub">{subtitle}</div> : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="lpv-side-card card border-0 shadow-sm mt-3">
                <h3 className="lpv-side-title">Trust &amp; recognition</h3>
                <div className="lpv-trust-stack">
                  {isVerified ? (
                    <div className="lpv-trust-card lpv-trust-card--blue">
                      <div className="lpv-trust-title">Troconet Verified</div>
                      <div className="lpv-trust-sub">KYC &amp; background check</div>
                    </div>
                  ) : null}
                  {(() => {
                    const list = profile?.certifications?.industryCertifications || [];
                    const hasIso = list.some((c) => String(c?.name || "").toLowerCase().includes("iso"));
                    return hasIso ? (
                      <div className="lpv-trust-card lpv-trust-card--green">
                        <div className="lpv-trust-title">ISO Certification</div>
                        <div className="lpv-trust-sub">Quality management</div>
                      </div>
                    ) : null;
                  })()}
                  {activeOnPlatform ? (
                    <div className="lpv-trust-card lpv-trust-card--purple">
                      <div className="lpv-trust-title">Active on Troconet</div>
                      <div className="lpv-trust-sub">Recent activity &amp; engagement</div>
                    </div>
                  ) : null}
                  {!isVerified && !activeOnPlatform && !(profile?.certifications?.industryCertifications || []).length ? (
                    <div className="text-muted small">No trust badges added yet.</div>
                  ) : null}
                </div>
              </div>
            </aside>
            )}
          </div>
        </>
      )}
    </div>
  );
}
